import React, { useMemo, useState, useEffect } from 'react';
import { Box, Typography, Chip, Card, Stack, CircularProgress, IconButton, Tooltip, Input, Button, Tabs, TabList, Tab, TabPanel, Switch } from '@mui/joy';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PrintIcon from '@mui/icons-material/Print';
import { DateTime } from 'luxon';
import { generateFeeds, type FunctionalClassification } from '../astro/feeds/generateFeedsV2';
import type { DashaContext, TransitContext, HouseScores, DashaLevel, HouseNum, PlanetName } from '../astro/feeds/types';
import { HOUSE_TO_LIFE } from '../astro/feeds/types';
import { NATAL_PREDICTION_RULES } from '../astro/feeds/rules';
import { TWO_PLANET_COMBINATIONS } from '../astro/feeds/two_planet_combinations';
import { sampleTransitsAtHouseChanges, aggregateTransitStats } from '../utils/transitSampling';
import type { AyanamsaProvider } from '../utils/ayanamsa';
import VimshottariDashaTimeline from './VimshottariDashaTimeline';
import { getCurrentDashaLevels } from '../utils/vimshottari';
import { getGrahaLongitudesAndMotionSidereal, getRahuKetuSidereal, assignPlanetsToHouses, getWholeSignCuspsSidereal, getAscendantSignAndNakshatra, NAKSHATRA_NAMES, RASHI_NAMES } from '../utils/astroEngine';
import { getAyanamsaProvider } from '../utils/ayanamsa';
import { getUserRuleCount } from '../astro/feeds/userRuleLoader';
import { storeYogasFromFeeds } from '../utils/yogaStorage';
import { supabase } from '../utils/supabaseClient';

type Props = {
  // Natal chart data
  natal: {
    placements: Record<string, { house: number; sign?: number }>;
    aspects: Array<{ from: string; toHouse: number }>;
    houseScores: HouseScores;
    cusps?: number[]; // Add cusps for transit sampling
  } | null;
  // Current transit data (optional)
  transit?: {
    placements: Record<string, { house: number; sign?: number }>;
  } | null;
  // Optional: union of transit positions sampled across active window
  transitPositions?: Array<{ planet: PlanetName; house: number; sign?: number; aspects?: number[] }>;
  // Functional classification for polarity gate
  functionalClassification?: FunctionalClassification;
  // Combustion map for transit penalty
  combustionMap?: Record<PlanetName, boolean>;
  // Debilitation and exaltation maps for yoga detection
  debilMap?: Partial<Record<PlanetName, boolean>>;
  exaltMap?: Partial<Record<PlanetName, boolean>>;
  // Dasha context (optional - if you want to show dasha-based feeds)
  dashaContext?: {
    MD: { planet: PlanetName; occupies: number; rules: number[]; aspects?: number[]; from?: string; to?: string; transitStats?: { beneficSupport: number; maleficPressure: number; avgTransits: number } };
    AD: { planet: PlanetName; occupies: number; rules: number[]; aspects?: number[]; from?: string; to?: string; transitStats?: { beneficSupport: number; maleficPressure: number; avgTransits: number } };
    PD: { planet: PlanetName; occupies: number; rules: number[]; aspects?: number[] };
    SD: { planet: PlanetName; occupies: number; rules: number[]; aspects?: number[] };
    window: { from: string; to: string };
  } | null;
  // Location for transit calculations
  location?: { lat: number; lon: number };
  // Ayanamsa provider
  ayanamsaProvider?: AyanamsaProvider;
  // Birth details for Vimshottari timeline
  birthDate?: string; // ISO date
  ascSign?: number; // 1-12
  // Blended % scores for planets
  blendedPct?: Partial<Record<PlanetName, number>>;
  // Gender for marriage karaka selection
  gender?: 'male' | 'female' | 'other';
  // User role for private feed filtering
  userRole?: 'admin' | 'member';
  // Kaal Sarp Yoga data
  kaalSarpYoga?: {
    isPresent: boolean;
    type?: string;
    severity?: 'partial' | 'full';
    details?: string;
    specificType?: string;
    specificEffects?: string;
    hasRajYogaCombination?: boolean;
    rahuHouse?: number;
    ketuHouse?: number;
    planetsBetween?: string[];
  } | null;
  // Callback to send active yogas to parent for NotificationCenter
  onYogasExtracted?: (yogas: any[]) => void;
  // Callback to send permanent natal yogas to parent (for report generation)
  onNatalYogasExtracted?: (yogas: any[]) => void;
};

function ordinal(n: number) {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
}

function fmtHuman(iso: string) {
  const dt = DateTime.fromISO(iso);
  if (!dt.isValid) return iso;
  const day = dt.day;
  const mon = dt.toFormat('MMM');
  const yr = dt.year;
  const time = dt.toFormat('h:mm a').toLowerCase();
  return `${day}${ordinal(day)} ${mon} ${yr} | ${time}`;
}

function FeedsList({ natal, transit, transitPositions, functionalClassification, combustionMap, debilMap, exaltMap, dashaContext, location, ayanamsaProvider, birthDate, ascSign, blendedPct, gender, userRole, kaalSarpYoga, onYogasExtracted, onNatalYogasExtracted }: Props) {
  // Shared date control state for all components
  const [center, setCenter] = useState<DateTime>(() => DateTime.now());
  const [yearsSpan, setYearsSpan] = useState<number>(12);
  
  // Tab state for feeds sections
  const [activeTab, setActiveTab] = useState<number>(0);
  
  // State for unique yogas toggle (Permanent Yogas tab)
  const [showUniqueOnly, setShowUniqueOnly] = useState<boolean>(false);
  
  // State for unique yogas toggle (Active Yogas & Predictions tab)
  const [showUniqueActiveOnly, setShowUniqueActiveOnly] = useState<boolean>(false);
  
  // State for async transit sampling results
  const [mdTransitStats, setMdTransitStats] = useState<{ loading: boolean; stats?: { jupiterBlessing: number; saturnPressure: number; avgTransits: number } }>({ loading: false });
  const [adTransitStats, setAdTransitStats] = useState<{ loading: boolean; stats?: { jupiterBlessing: number; saturnPressure: number; avgTransits: number } }>({ loading: false });

  // Track custom rule count
  const [customRuleCount, setCustomRuleCount] = useState<number>(0);

  // Saturn sign transits for Sade Sati (ephemeris-based)
  const [saturnTransits, setSaturnTransits] = useState<{ currentSign: number; ingressDate: string; egressDate: string } | null>(null);

  useEffect(() => {
    getUserRuleCount().then(setCustomRuleCount);
  }, []);

  // PDF export function - expands all evidences before printing
  const handleExportPDF = () => {
    // Open all details elements
    const detailsElements = document.querySelectorAll('details');
    const wasOpen: boolean[] = [];
    
    detailsElements.forEach((details, index) => {
      wasOpen[index] = details.open;
      details.open = true;
    });

    // Trigger print dialog
    setTimeout(() => {
      window.print();
      
      // Restore original state after print
      setTimeout(() => {
        detailsElements.forEach((details, index) => {
          details.open = wasOpen[index];
        });
      }, 100);
    }, 100);
  };

  // Fetch Saturn sign transits when location is available
  useEffect(() => {
    if (!location) return;
    
    import('../utils/saturnTransits').then(({ calculateSaturnSignTransits }) => {
      calculateSaturnSignTransits(DateTime.now(), location)
        .then(result => {
          if (result) {
            setSaturnTransits(result);
          }
        })
        .catch(() => {
          // Use ~2.5 year approximation if calculation fails
        });
    });
  }, [location]);

  // Build a DashaContext based on the selected center date
  const dashaCtxUsed = useMemo<DashaContext | null>(() => {
    if (!natal || !birthDate || !location) return null;
    
    // Ensure natal has all required placements before proceeding
    if (!natal.placements || !natal.placements.Sun || !natal.placements.Moon || !natal.placements.Jupiter) {
      return null;
    }

    try {
      // Calculate Moon's sidereal longitude from birth date
      const provider = ayanamsaProvider ?? getAyanamsaProvider('lahiri');
      const grahas = getGrahaLongitudesAndMotionSidereal({ isoDate: birthDate, latitude: location.lat, longitude: location.lon }, provider) as any;
      const moonLonSidereal = grahas?.Moon?.lon;
      if (!moonLonSidereal) return null;

      // Get dasha levels for the center date
      const centerISO = center.toISO();
      if (!centerISO) return null;
      
      const levels = getCurrentDashaLevels(birthDate, moonLonSidereal, centerISO);
      if (!levels || !levels.md || !levels.ad || !levels.pd || !levels.sd) return null;

      // Get natal placements and aspects for house scores
      const nodes = getRahuKetuSidereal({ isoDate: birthDate, latitude: location.lat, longitude: location.lon }, provider) as any;
      const asc = getAscendantSignAndNakshatra(birthDate, location.lat, location.lon);
      const cusps = getWholeSignCuspsSidereal(asc.siderealLongitudeDeg);
      
      const longs: any = {
        Sun: grahas?.Sun?.lon, Moon: grahas?.Moon?.lon, Mars: grahas?.Mars?.lon, 
        Mercury: grahas?.Mercury?.lon, Jupiter: grahas?.Jupiter?.lon, Venus: grahas?.Venus?.lon, 
        Saturn: grahas?.Saturn?.lon, Rahu: nodes?.Rahu, Ketu: nodes?.Ketu,
      };
      const placements = assignPlanetsToHouses(cusps, longs);

      // Helper to get houses ruled by a planet based on ascendant sign
      const getRuledHouses = (planet: string): number[] => {
        // Sign lords (natural zodiac): 1=Mars, 2=Venus, 3=Mercury, 4=Moon, 5=Sun, 6=Mercury, 
        // 7=Venus, 8=Mars, 9=Jupiter, 10=Saturn, 11=Saturn, 12=Jupiter
        const SIGN_LORDS: Record<number, string> = {
          1: 'Mars', 2: 'Venus', 3: 'Mercury', 4: 'Moon', 5: 'Sun', 6: 'Mercury',
          7: 'Venus', 8: 'Mars', 9: 'Jupiter', 10: 'Saturn', 11: 'Saturn', 12: 'Jupiter'
        };
        
        const ascSign = asc.ascendantSignIndex; // 1-12 (Aries=1, Taurus=2, ..., Cancer=4, etc.)
        const ruledHouses: number[] = [];
        
        // For each house (1-12), calculate which sign it corresponds to, then check if planet rules it
        for (let house = 1; house <= 12; house++) {
          // House 1 = ascendant sign, House 2 = next sign, etc.
          const signForHouse = ((ascSign - 1 + house - 1) % 12) + 1;
          if (SIGN_LORDS[signForHouse] === planet) {
            ruledHouses.push(house);
          }
        }
        
        return ruledHouses;
      };

      // Helper to get aspects (basic 7th aspect for all, plus special aspects)
      const getAspects = (planet: string, house: number): number[] => {
        const seventh = ((house - 1 + 6) % 12) + 1;
        const aspects = [seventh];
        
        if (planet === 'Mars') {
          aspects.push(((house - 1 + 3) % 12) + 1); // 4th
          aspects.push(((house - 1 + 7) % 12) + 1); // 8th
        } else if (planet === 'Jupiter') {
          aspects.push(((house - 1 + 4) % 12) + 1); // 5th
          aspects.push(((house - 1 + 8) % 12) + 1); // 9th
        } else if (planet === 'Saturn') {
          aspects.push(((house - 1 + 2) % 12) + 1); // 3rd
          aspects.push(((house - 1 + 9) % 12) + 1); // 10th
        }
        
        return aspects;
      };

      // Build DashaContext
      // Filter out Rahu and Ketu from functional classification (only 7 planets have functional roles)
      const functionalFiltered = functionalClassification
        ? Object.fromEntries(
            Object.entries(functionalClassification).filter(([planet]) => planet !== 'Rahu' && planet !== 'Ketu')
          ) as Partial<Record<Exclude<PlanetName, 'Rahu' | 'Ketu'>, 'benefic' | 'malefic' | 'neutral'>>
        : undefined;

      const dashaCtx: DashaContext = {
        window: { from: levels.md.start, to: levels.md.end },
        houseScores: natal.houseScores,
        lagnaSign: asc.ascendantSignIndex, // Add lagnaSign for natal rule calculations
        birthDate: birthDate, // Add birthDate for age calculation
        gender: gender, // Add gender for karaka selection
        // Add combustion map from natal data
        combustionMap: combustionMap,
        // Add functional classification (filtered to exclude Rahu/Ketu)
        functional: functionalFiltered,
        // Add blended planet percentages
        blendedPct: blendedPct,
        // Add natal placements for all planets (for yoga detection)
        natalPlacements: {
          Sun: { house: natal.placements['Sun']?.house as HouseNum, sign: natal.placements['Sun']?.sign, aspects: getAspects('Sun', natal.placements['Sun']?.house) as HouseNum[], rules: getRuledHouses('Sun') as HouseNum[], debilitated: debilMap?.Sun, exalted: exaltMap?.Sun },
          Moon: (() => {
            const moonSign = natal.placements['Moon']?.sign;
            console.log('[FeedsList] Natal Moon sign from natal.placements:', moonSign, 'Moon house:', natal.placements['Moon']?.house);
            return { house: natal.placements['Moon']?.house as HouseNum, sign: moonSign, aspects: getAspects('Moon', natal.placements['Moon']?.house) as HouseNum[], rules: getRuledHouses('Moon') as HouseNum[], debilitated: debilMap?.Moon, exalted: exaltMap?.Moon };
          })(),
          Mars: { house: natal.placements['Mars']?.house as HouseNum, sign: natal.placements['Mars']?.sign, aspects: getAspects('Mars', natal.placements['Mars']?.house) as HouseNum[], rules: getRuledHouses('Mars') as HouseNum[], debilitated: debilMap?.Mars, exalted: exaltMap?.Mars },
          Mercury: { house: natal.placements['Mercury']?.house as HouseNum, sign: natal.placements['Mercury']?.sign, aspects: getAspects('Mercury', natal.placements['Mercury']?.house) as HouseNum[], rules: getRuledHouses('Mercury') as HouseNum[], debilitated: debilMap?.Mercury, exalted: exaltMap?.Mercury },
          Jupiter: { house: natal.placements['Jupiter']?.house as HouseNum, sign: natal.placements['Jupiter']?.sign, aspects: getAspects('Jupiter', natal.placements['Jupiter']?.house) as HouseNum[], rules: getRuledHouses('Jupiter') as HouseNum[], debilitated: debilMap?.Jupiter, exalted: exaltMap?.Jupiter },
          Venus: { house: natal.placements['Venus']?.house as HouseNum, sign: natal.placements['Venus']?.sign, aspects: getAspects('Venus', natal.placements['Venus']?.house) as HouseNum[], rules: getRuledHouses('Venus') as HouseNum[], debilitated: debilMap?.Venus, exalted: exaltMap?.Venus },
          Saturn: { house: natal.placements['Saturn']?.house as HouseNum, sign: natal.placements['Saturn']?.sign, aspects: getAspects('Saturn', natal.placements['Saturn']?.house) as HouseNum[], rules: getRuledHouses('Saturn') as HouseNum[], debilitated: debilMap?.Saturn, exalted: exaltMap?.Saturn },
          Rahu: { house: natal.placements['Rahu']?.house as HouseNum, sign: natal.placements['Rahu']?.sign, aspects: [], rules: getRuledHouses('Rahu') as HouseNum[] },
          Ketu: { house: natal.placements['Ketu']?.house as HouseNum, sign: natal.placements['Ketu']?.sign, aspects: [], rules: getRuledHouses('Ketu') as HouseNum[] },
        },
        levels: [
          {
            level: 'MD',
            planet: levels.md.lord as PlanetName,
            from: levels.md.start,
            to: levels.md.end,
            rules: getRuledHouses(levels.md.lord) as HouseNum[],
            occupies: natal.placements[levels.md.lord]?.house as HouseNum,
            aspects: getAspects(levels.md.lord, natal.placements[levels.md.lord]?.house) as HouseNum[],
          },
          {
            level: 'AD',
            planet: levels.ad.lord as PlanetName,
            from: levels.ad.start,
            to: levels.ad.end,
            rules: getRuledHouses(levels.ad.lord) as HouseNum[],
            occupies: natal.placements[levels.ad.lord]?.house as HouseNum,
            aspects: getAspects(levels.ad.lord, natal.placements[levels.ad.lord]?.house) as HouseNum[],
          },
          {
            level: 'PD',
            planet: levels.pd.lord as PlanetName,
            from: levels.pd.start,
            to: levels.pd.end,
            rules: getRuledHouses(levels.pd.lord) as HouseNum[],
            occupies: natal.placements[levels.pd.lord]?.house as HouseNum,
            aspects: getAspects(levels.pd.lord, natal.placements[levels.pd.lord]?.house) as HouseNum[],
          },
          {
            level: 'SD',
            planet: levels.sd.lord as PlanetName,
            from: levels.sd.start,
            to: levels.sd.end,
            rules: getRuledHouses(levels.sd.lord) as HouseNum[],
            occupies: natal.placements[levels.sd.lord]?.house as HouseNum,
            aspects: getAspects(levels.sd.lord, natal.placements[levels.sd.lord]?.house) as HouseNum[],
          },
        ] as [DashaLevel, DashaLevel, DashaLevel, DashaLevel],
      };

      return dashaCtx;
    } catch (error) {
      console.error('[FeedsList] Failed to calculate dasha for center date:', error);
      return null;
    }
  }, [natal, birthDate, location, center, ayanamsaProvider]);

  // Async effect: Compute MD transit stats with smart sampling
  useEffect(() => {
    if (!dashaCtxUsed || !natal?.cusps || !location || !ayanamsaProvider) {
      return;
    }

    setMdTransitStats({ loading: true });

    const mdLevel = dashaCtxUsed.levels.find(l => l.level === 'MD');
    if (!mdLevel) return;

    const mdHouses = [...mdLevel.rules, mdLevel.occupies, ...(mdLevel.aspects || [])];
    const uniqueHouses = Array.from(new Set(mdHouses)) as number[];

    sampleTransitsAtHouseChanges(
      mdLevel.from,
      mdLevel.to,
      natal.cusps,
      location,
      ayanamsaProvider,
      30
    ).then(snapshots => {
      const stats = aggregateTransitStats(snapshots, uniqueHouses, functionalClassification, blendedPct);
      setMdTransitStats({
        loading: false,
        stats: {
          jupiterBlessing: stats.beneficBlessingPercent,
          saturnPressure: stats.maleficPressurePercent,
          avgTransits: Math.round(stats.avgTransitsOnTarget * 10) / 10
        }
      });
    }).catch(err => {
      console.error('[FeedsList] MD transit sampling failed', err);
      setMdTransitStats({ loading: false });
    });
  }, [dashaCtxUsed, natal?.cusps, location, ayanamsaProvider, functionalClassification, blendedPct]);

  // Async effect: Compute AD transit stats with smart sampling
  useEffect(() => {
    if (!dashaCtxUsed || !natal?.cusps || !location || !ayanamsaProvider) {
      return;
    }

    setAdTransitStats({ loading: true });

    const adLevel = dashaCtxUsed.levels.find(l => l.level === 'AD');
    if (!adLevel) return;

    const adHouses = [...adLevel.rules, adLevel.occupies, ...(adLevel.aspects || [])];
    const uniqueHouses = Array.from(new Set(adHouses)) as number[];

    sampleTransitsAtHouseChanges(
      adLevel.from,
      adLevel.to,
      natal.cusps,
      location,
      ayanamsaProvider,
      30
    ).then(snapshots => {
      const stats = aggregateTransitStats(snapshots, uniqueHouses, functionalClassification, blendedPct);
      setAdTransitStats({
        loading: false,
        stats: {
          jupiterBlessing: stats.beneficBlessingPercent,
          saturnPressure: stats.maleficPressurePercent,
          avgTransits: Math.round(stats.avgTransitsOnTarget * 10) / 10
        }
      });
    }).catch(err => {
      console.error('[FeedsList] AD transit sampling failed', err);
      setAdTransitStats({ loading: false });
    });
  }, [dashaCtxUsed, natal?.cusps, location, ayanamsaProvider, functionalClassification, blendedPct]);

  const feeds = useMemo(() => {
    if (!natal || !dashaCtxUsed) return [];
    
    // Build transit context from provided union positions or current snapshot
    const positions = transitPositions && transitPositions.length > 0
      ? transitPositions.map(p => ({ planet: p.planet, house: p.house as HouseNum, sign: (p as any).sign, aspects: p.aspects as HouseNum[] | undefined }))
      : (transit
        ? Object.entries(transit.placements)
            .filter(([_, p]) => p?.house)
            .map(([planet, p]) => ({ planet: planet as PlanetName, house: p.house as HouseNum, sign: p.sign }))
        : []);

    // Compute activation: planets transiting natal occupied house of active dasha planets
    const activation: Record<PlanetName, PlanetName[]> = {} as any;
    for (const lvl of dashaCtxUsed.levels) {
      const occ = lvl.occupies as HouseNum;
      const hits = positions.filter(p => p.house === occ).map(p => p.planet);
      if (hits.length) activation[lvl.planet] = Array.from(new Set(hits));
    }

    const transitCtx: TransitContext = {
      positions: positions as any,
      planetsTransitingDashaOccupied: activation,
    };
    
    // Use V2 generator with functional classification
    const functional: FunctionalClassification = functionalClassification || ({} as FunctionalClassification);
    
    const generatedFeeds = generateFeeds({
      dasha: dashaCtxUsed,
      transits: transitCtx,
      functional,
      combustionMap
    });
    
    // Add Kaal Sarp Yoga if present (permanent natal yoga)
    if (kaalSarpYoga && kaalSarpYoga.isPresent) {
      let message = kaalSarpYoga.details || `Kaal Sarp Yoga detected (${kaalSarpYoga.type || 'unknown'} type)`;
      
      // Add specific type and effects if available
      if (kaalSarpYoga.specificType) {
        message = `${kaalSarpYoga.specificType}: ${kaalSarpYoga.specificEffects || kaalSarpYoga.details}`;
        
        if (kaalSarpYoga.hasRajYogaCombination) {
          message += ' | RAJ YOGA SPECIAL: Rahu-Venus combination creates material success but may challenge relationships.';
        }
      }
      
      const evidence = [
        `Kaal Sarp Type: ${kaalSarpYoga.specificType || kaalSarpYoga.type || 'unknown'}`,
        `Rahu in House ${kaalSarpYoga.rahuHouse}, Ketu in House ${kaalSarpYoga.ketuHouse}`,
        `Planets between: ${kaalSarpYoga.planetsBetween?.join(', ') || 'all'}`,
        kaalSarpYoga.details || ''
      ];
      
      if (kaalSarpYoga.specificEffects) {
        evidence.push(`Effects: ${kaalSarpYoga.specificEffects}`);
      }
      
      generatedFeeds.push({
        id: 'kaal_sarp_yoga',
        ruleKey: 'kaal_sarp_yoga',
        message,
        lifeArea: 'karma' as any,
        severity: 'alert',  // Kaal Sarp Yoga is always a malefic/alert yoga
        score: kaalSarpYoga.type === 'full' ? -50 : -30,
        window: null,
        evidence,
        priority: 90
      } as any);
    }
    
    return generatedFeeds;
  }, [natal, transit, transitPositions, functionalClassification, combustionMap, dashaCtxUsed, kaalSarpYoga]);

  // Separate feeds into natal/yogas (permanent), timing (dasha-based), and natal predictions (planet combinations)
  const { natalYogaFeeds, timingFeeds, natalPredictionFeeds } = useMemo(() => {
    const natalYoga: typeof feeds = [];
    const timing: typeof feeds = [];
    const natalPredictions: typeof feeds = [];
    
    // Natal prediction rule keys (planet combinations - goes to Predictions tab)
    // Automatically include ALL rules from NATAL_PREDICTION_RULES array + two-planet combinations
    const natalPredictionKeys = new Set([
      ...NATAL_PREDICTION_RULES.map(rule => rule.key),
      ...TWO_PLANET_COMBINATIONS.map(rule => rule.key),
      // Saturn conjunction analysis (from rules.ts)
      'saturn_sun_conjunction',
      'saturn_moon_conjunction',
      'saturn_mars_conjunction',
      'saturn_mercury_conjunction',
      'saturn_jupiter_conjunction',
      'saturn_venus_conjunction',
      'saturn_rahu_conjunction',
      'saturn_ketu_conjunction',
    ]);
    
    // Natal-only yoga rule keys (yogas that exist in chart permanently, shown ONLY in natal tab)
    const natalOnlyYogaKeys = new Set([
      'kaal_sarp_yoga',
      'foreign_settlement_yoga',
      'dharma_karma_raj_yoga_natal',
      'kendra_trikona_raj_yoga_natal',
      'gaja_kesari_yoga_natal',
      'budhaditya_yoga_natal',
      'malavya_yoga_natal',
      'rahu_alone_raj_yoga',
      'neech_bhang_dispositor',           // Neech Bhang Rule 1
      'neech_bhang_exaltation_lord',      // Neech Bhang Rule 2
      'neech_bhang_mutual_aspect',        // Neech Bhang Rule 3
      'neech_bhang_dispositor_kendra',    // Neech Bhang Rule 4
      'saturn_ketu_position',             // Saturn analysis by Ketu position
      'criminal_mindset_rahu_conjunction',     // PRIVATE natal prediction
      'rapist_mindset_8th_affliction',         // PRIVATE natal prediction
      // Classical BPHS Yogas
      'vipareeta_raj_yoga',
      'chandra_mangala_yoga',
      'amala_yoga',
      'sunapha_yoga',
      'anapha_yoga',
      'durudhara_yoga',
      'parivartana_yoga',
      'kahala_yoga',
      'parvata_yoga',
      'saraswati_yoga',
      'vasumati_yoga',
      'grahan_yoga',
      'dur_yoga',
      'adhi_yoga',  // Also in activatedYogaKeys for dual display
      'kemdruma_yoga',
      'kalanidhi_yoga',
      'lakshmi_yoga',  // Also in activatedYogaKeys for dual display
      // Mahapurusha yogas (should appear as permanent natal features)
      'hamsa_yoga',
      'shasha_yoga',
      'ruchaka_yoga',
      'bhadra_yoga',
      // Marriage yogas
      'rahu_ninth_intercaste_yoga',  // Rahu in 9th house - intercaste marriage yoga
      // Add more _natal yoga keys here as we create them
    ]);
    
    // Natal rule keys (permanent natal conditions excluding yogas)
    const natalRuleKeys = new Set([
      'secrecy_principle_8th_12th_lord',
      'trishadaya_upachaya_strength',
      'malefic_dusthana_from_own_house',
      'combustion_affliction'
    ]);
    
    // Dual-purpose Yoga rule keys (yogas shown in BOTH tabs: natal as permanent, timing when activated)
    // These represent yogas that exist in the chart AND are activated by current dasha
    const activatedYogaKeys = new Set([
      'dharma_karma_raj_yoga',
      'lakshmi_yoga',
      'gaja_kesari_yoga',
      'adhi_yoga',
      'budhaditya_yoga',
      'hamsa_yoga',
      'shasha_yoga',
      'ruchaka_yoga',
      'malavya_yoga',
      'bhadra_yoga',
      'rahu_ketu_kendra_trikona_yoga_natal',
      // Doshas (show in both Doshas tab AND Active Yogas when manifesting)
      'mangal_dosha_natal',
      'kaal_sarp_yoga',
      // Marriage yogas (show in both when activated)
      'rahu_ninth_intercaste_yoga',  // Shows in Active when Rahu/7th lord dasha
      'rahu_ninth_intercaste_yoga_timing',  // Timing version
      // Classical yogas that should also show when activated
      'vasumati_yoga',
      'grahan_yoga',
      'dur_yoga',
      'kemdruma_yoga',
      'kalanidhi_yoga',
      'vipareeta_raj_yoga',
      'chandra_mangala_yoga',
      'amala_yoga',
      'sunapha_yoga',
      'anapha_yoga',
      'durudhara_yoga',
      'parivartana_yoga',
      'kahala_yoga',
      'parvata_yoga',
      'saraswati_yoga'
    ]);
    
    // Timing-only yoga keys (time-based yogas like Sade Sati phases)
    const timingOnlyYogaKeys = new Set([
      'sade_sati_rising',
      'sade_sati_peak',
      'sade_sati_setting'
    ]);
    
    feeds.forEach(feed => {
      // Filter private feeds - only show to admins
      if (feed.private && userRole !== 'admin') {
        return; // Skip this feed for non-admin users
      }
      
      // Classification logic:
      // 1. Natal predictions (planet combinations) → predictions tab, also timing if activated
      if (natalPredictionKeys.has(feed.ruleKey || '')) {
        natalPredictions.push(feed);
        // Also add doshas to timing tab if they're in activatedYogaKeys (dual display)
        if (activatedYogaKeys.has(feed.ruleKey || '')) {
          timing.push(feed);
        }
      }
      // 2. Natal-only yogas and conditions → natal tab (may also go to timing if in activatedYogaKeys)
      else if (natalOnlyYogaKeys.has(feed.ruleKey || '') || natalRuleKeys.has(feed.ruleKey || '')) {
        natalYoga.push(feed);
        // Also add to timing if it's an activated yoga (dual display)
        if (activatedYogaKeys.has(feed.ruleKey || '')) {
          timing.push(feed);
        }
      }
      // 3. Activated yogas (Raj/Mahapurusha without _natal suffix) → timing tab ONLY (unless already handled above)
      // NOTE: We now have separate _natal versions for permanent display, so timing versions go to timing only
      else if (activatedYogaKeys.has(feed.ruleKey || '')) {
        timing.push(feed);     // Show in timing (the yoga is activated by dasha)
      }
      // 4. Timing-only yogas (Sade Sati) → timing tab only
      else if (timingOnlyYogaKeys.has(feed.ruleKey || '')) {
        timing.push(feed);
      }
      // 5. Default: timing section for other dasha-based predictions
      else {
        timing.push(feed);
      }
    });
    
    console.log('[FeedsList Classification Results]', {
      totalFeeds: feeds.length,
      natalYoga: natalYoga.length,
      natalPredictions: natalPredictions.length,
      timing: timing.length,
      seventhLordInFeeds: feeds.filter(f => f.ruleKey?.includes('seventh_lord')).length,
      seventhLordInNatalPredictions: natalPredictions.filter(f => f.ruleKey?.includes('seventh_lord')).length
    });
    
    // Sort timing feeds to prioritize doshas at the top
    const sortedTiming = timing.sort((a, b) => {
      const aIsDosha = a.ruleKey?.includes('dosha') || a.message?.toLowerCase().includes('dosha');
      const bIsDosha = b.ruleKey?.includes('dosha') || b.message?.toLowerCase().includes('dosha');
      
      if (aIsDosha && !bIsDosha) return -1; // a comes first
      if (!aIsDosha && bIsDosha) return 1;  // b comes first
      return 0; // maintain original order for non-doshas
    });
    
    return { natalYogaFeeds: natalYoga, timingFeeds: sortedTiming, natalPredictionFeeds: natalPredictions };
  }, [feeds, userRole]);

  // Send active yogas (timingFeeds) to parent for NotificationCenter
  useEffect(() => {
    if (onYogasExtracted) {
      onYogasExtracted(timingFeeds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timingFeeds]); // Only re-run when timingFeeds changes, not when callback reference changes

  // Send permanent natal yogas + planetary combinations to parent (for report generation)
  // Both natalYogaFeeds (tab 1: Permanent Features & Yogas) and natalPredictionFeeds
  // (tab: Planetary Combinations) are permanent natal features and must appear in the
  // Classical Yogas section of the report. The report builder cross-references activeYogasFromFeeds
  // to set isActive:true on whichever items are currently triggered by the running dasha.
  useEffect(() => {
    if (onNatalYogasExtracted && (natalYogaFeeds.length > 0 || natalPredictionFeeds.length > 0)) {
      onNatalYogasExtracted([...natalYogaFeeds, ...natalPredictionFeeds]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [natalYogaFeeds, natalPredictionFeeds]);

  // Store permanent yogas in database after they're computed
  useEffect(() => {
    if (!natalYogaFeeds || natalYogaFeeds.length === 0 || !natal) return;
    
    const storeYogas = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        
        // Create chart snapshot from natal data
        const chartSnapshot = {
          placements: natal.placements,
          cusps: natal.cusps,
          timestamp: new Date().toISOString()
        };
        
        const result = await storeYogasFromFeeds(natalYogaFeeds, user.id, null, chartSnapshot);
        
        if (!result.success) {
          console.error('[FeedsList] Failed to store yogas:', result.errors);
        }
      } catch (err) {
        console.error('[FeedsList] Error storing yogas:', err);
      }
    };
    
    // Store in background (don't block UI)
    storeYogas();
  }, [natalYogaFeeds, natal]);

  // Helper: Calculate theme scores for MD/AD
  // For now, uses CURRENT transit influence (from active SD/PD window)
  // TODO: Implement smart house-change sampling for long-period transit averages
  // See src/utils/transitSampling.ts for event-based sampling approach
  const calculateThemeScore = (level: DashaLevel, transitCtx: TransitContext) => {
    const houses = [...level.rules, level.occupies, ...(level.aspects || [])];
    const uniqueHouses = Array.from(new Set(houses)) as HouseNum[];
    
    if (uniqueHouses.length === 0) return { baseImpact: 0, transitPoints: 0 };
    
    // Calculate base impact (simplified - using mean house score)
    const houseScores: Record<number, number> = natal?.houseScores || {};
    const scores = uniqueHouses.map(h => houseScores[h] || 0);
    const meanScore = scores.reduce((a, b) => a + b, 0) / scores.length;
    
    // Get functional classification
    const functional = functionalClassification || ({} as FunctionalClassification);
    const classification = functional[level.planet] || 'neutral';
    
    // Apply context-aware interaction (simplified version)
    const normalized = Math.max(-1, Math.min(1, meanScore / 100));
    const isAfflicted = normalized < 0;
    const absScore = Math.abs(normalized);
    
    let adjusted = normalized;
    if (classification === 'malefic') {
      adjusted = isAfflicted ? -absScore * 1.2 : absScore * 0.8;
    } else if (classification === 'benefic' || classification === 'yoga_karaka') {
      adjusted = isAfflicted ? absScore * 0.6 : absScore * 1.1;
    }
    
    const baseImpact = adjusted * 100; // MD/AD have 0 weight in events but show theme
    
    // Calculate transit reinforcement points
    let transitPoints = 0;
    const positions = transitCtx.positions || [];
    
    // Jupiter bonus
    const jupiterPos = positions.find(p => p.planet === 'Jupiter');
    if (jupiterPos) {
      const jupTrines = [jupiterPos.house, ((jupiterPos.house - 1 + 4) % 12 + 1) as HouseNum, ((jupiterPos.house - 1 + 8) % 12 + 1) as HouseNum];
      if (uniqueHouses.some(h => jupTrines.includes(h))) transitPoints += 12;
    }
    
    // Saturn penalty
    const saturnPos = positions.find(p => p.planet === 'Saturn');
    if (saturnPos && uniqueHouses.includes(saturnPos.house)) transitPoints -= 12;
    
    // Transits over dasha planet's natal house
    const transitingOccupied = positions.filter(p => p.house === level.occupies);
    transitPoints += transitingOccupied.length * 3; // +3 per planet
    
    return { baseImpact: Math.round(baseImpact), transitPoints: Math.round(transitPoints) };
  };

  const transitCtx = useMemo<TransitContext>(() => {
    if (!natal || !dashaCtxUsed) return { 
      positions: [], 
      planetsTransitingDashaOccupied: {
        Sun: [], Moon: [], Mercury: [], Venus: [], Mars: [], 
        Jupiter: [], Saturn: [], Rahu: [], Ketu: []
      } as Record<PlanetName, PlanetName[]>
    };
    
    const positions = transitPositions && transitPositions.length > 0
      ? transitPositions.map(p => ({ planet: p.planet, house: p.house as HouseNum, sign: (p as any).sign, aspects: p.aspects as HouseNum[] | undefined }))
      : (transit
        ? Object.entries(transit.placements)
            .filter(([_, p]) => p?.house)
            .map(([planet, p]) => ({ planet: planet as PlanetName, house: p.house as HouseNum, sign: p.sign }))
        : []);

    const activation: Record<PlanetName, PlanetName[]> = {
      Sun: [], Moon: [], Mercury: [], Venus: [], Mars: [], 
      Jupiter: [], Saturn: [], Rahu: [], Ketu: []
    };
    
    for (const lvl of dashaCtxUsed.levels) {
      const occ = lvl.occupies as HouseNum;
      const hits = positions.filter(p => p.house === occ).map(p => p.planet);
      if (hits.length) activation[lvl.planet] = Array.from(new Set(hits));
    }

    return {
      positions: positions as any,
      planetsTransitingDashaOccupied: activation,
      saturnSignTransits: saturnTransits || undefined
    };
  }, [natal, transit, transitPositions, dashaCtxUsed, saturnTransits]);

  // Group natal/yoga feeds by life area with optional unique filtering
  const groupedNatalYoga = useMemo(() => {
    let feedsToGroup = natalYogaFeeds;
    
    // Filter for unique yogas if toggle is on
    if (showUniqueOnly) {
      const seen = new Set<string>();
      feedsToGroup = natalYogaFeeds.filter(feed => {
        const key = feed.ruleKey || feed.message.split(':')[0];
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    }
    
    return feedsToGroup.reduce((acc, feed) => {
      if (!acc[feed.lifeArea]) acc[feed.lifeArea] = [];
      acc[feed.lifeArea].push(feed);
      return acc;
    }, {} as Record<string, typeof natalYogaFeeds>);
  }, [natalYogaFeeds, showUniqueOnly]);

  // Group timing feeds by life area with optional unique filtering
  const groupedTiming = useMemo(() => {
    let feedsToGroup = timingFeeds;
    
    // Filter for unique yogas if toggle is on
    if (showUniqueActiveOnly) {
      const seen = new Set<string>();
      feedsToGroup = timingFeeds.filter(feed => {
        const key = feed.ruleKey || feed.message.split(':')[0];
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    }
    
    return feedsToGroup.reduce((acc, feed) => {
      if (!acc[feed.lifeArea]) acc[feed.lifeArea] = [];
      acc[feed.lifeArea].push(feed);
      return acc;
    }, {} as Record<string, typeof feedsToGroup>);
  }, [timingFeeds, showUniqueActiveOnly]);

  // Group natal prediction feeds by life area
  const groupedNatalPredictions = useMemo(() => {
    // Exclude major doshas (Mangal Dosha) from planetary combinations section as they're shown separately
    const filteredFeeds = natalPredictionFeeds.filter(f => f.ruleKey !== 'mangal_dosha_natal');
    
    return filteredFeeds.reduce((acc, feed) => {
      if (!acc[feed.lifeArea]) acc[feed.lifeArea] = [];
      acc[feed.lifeArea].push(feed);
      return acc;
    }, {} as Record<string, typeof natalPredictionFeeds>);
  }, [natalPredictionFeeds]);

  if (!natal) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography level="body-lg" color="neutral">
          Load a birth chart to see personalized feeds
        </Typography>
      </Box>
    );
  }

  // Prepare dasha header display
  const displayLevels: Array<{ level: DashaLevel['level']; planet: PlanetName; from?: string; to?: string }>
    = (dashaCtxUsed?.levels ?? []).map(lvl => ({ level: lvl.level, planet: lvl.planet, from: (lvl as any).from, to: (lvl as any).to })) as any;

  // Show MD/AD theme cards even if no PD/SD events detected
  const hasMDAThemes = dashaCtxUsed && displayLevels.length >= 2;

  const severityColor = (severity: string) => {
    if (severity === 'alert') return 'danger';
    if (severity === 'caution') return 'warning';
    if (severity === 'mixed') return 'neutral';
    return 'success';
  };

  const severityLabel = (severity: string) => {
    if (severity === 'alert') return '🔴 Alert';
    if (severity === 'caution') return '⚠️ Caution';
    if (severity === 'mixed') return '⚡ Mixed';
    return '✨ Positive';
  };

  // Navigation helpers
  const jump = (delta: { years?: number; months?: number }) => setCenter(c => c.plus(delta));
  const jumpToday = () => setCenter(DateTime.now());

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      {/* Page heading with inline date controls */}
      <Box id="section-personalized-feeds" sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, mb: 2, flexWrap: 'wrap' }}>
        <Typography level="h3" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          Personalized Astrology Feeds
        </Typography>
        
        {customRuleCount > 0 && (
          <Chip size="sm" variant="soft" color="primary">
            {customRuleCount} custom rule{customRuleCount > 1 ? 's' : ''} active
          </Chip>
        )}
        
        <Box sx={{ flex: 1 }} />
        
        {/* Date Control Panel */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: { xs: 0.5, sm: 1 }, 
          flexWrap: 'wrap',
          width: { xs: '100%', sm: 'auto' },
          justifyContent: { xs: 'center', sm: 'flex-end' }
        }}>
          <Tooltip title="Back one year">
            <IconButton size="sm" variant="outlined" onClick={() => jump({ years: -1 })} sx={{ minWidth: { xs: 32, sm: 36 } }}>−Y</IconButton>
          </Tooltip>
          <Tooltip title="Back one month">
            <IconButton size="sm" variant="outlined" onClick={() => jump({ months: -1 })} sx={{ minWidth: { xs: 32, sm: 36 } }}>−M</IconButton>
          </Tooltip>
          <Button size="sm" variant="soft" onClick={jumpToday} sx={{ minWidth: { xs: 60, sm: 70 } }}>Today</Button>
          <Tooltip title="Forward one month">
            <IconButton size="sm" variant="outlined" onClick={() => jump({ months: +1 })} sx={{ minWidth: { xs: 32, sm: 36 } }}>+M</IconButton>
          </Tooltip>
          <Tooltip title="Forward one year">
            <IconButton size="sm" variant="outlined" onClick={() => jump({ years: +1 })} sx={{ minWidth: { xs: 32, sm: 36 } }}>+Y</IconButton>
          </Tooltip>
          
          <Input
            size="sm"
            type="date"
            value={center.toISODate()!}
            onChange={(e) => {
              const v = e.target.value;
              if (v) setCenter(DateTime.fromISO(v));
            }}
            sx={{ width: { xs: 140, sm: 180 }, display: { xs: 'none', sm: 'block' } }}
          />

          <Typography level="body-sm" sx={{ opacity: 0.7, ml: { xs: 0, sm: 1 }, display: { xs: 'none', sm: 'block' } }}>Span:</Typography>
          <Button size="sm" variant="outlined" onClick={() => setYearsSpan(y => Math.max(3, y - 3))} sx={{ minWidth: { xs: 35, sm: 45 } }}>−3y</Button>
          <Typography level="body-sm" sx={{ minWidth: 30, textAlign: 'center' }}>{yearsSpan * 2}y</Typography>
          <Button size="sm" variant="outlined" onClick={() => setYearsSpan(y => Math.min(60, y + 3))} sx={{ minWidth: { xs: 35, sm: 45 } }}>+3y</Button>
        </Box>
      </Box>

      {/* Top row: Timeline + MD Card + AD Card in 3-column grid */}
      {birthDate && location && ascSign && displayLevels && displayLevels.length > 0 && (
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, 
          gap: 2, 
          mb: 3,
          p: 2,
          borderRadius: 2,
          bgcolor: 'background.level1'
        }}>

          {/* Vimshottari Timeline */}
          <Box sx={{ 
            minWidth: 0, 
            overflow: 'hidden',
            bgcolor: 'background.surface',
            borderRadius: 2,
            p: 1.5,
            boxShadow: 'sm'
          }}>
            <VimshottariDashaTimeline
              isoDate={birthDate}
              latitude={location.lat}
              longitude={location.lon}
              ascSign={ascSign}
              blendedPct={blendedPct}
              showFocusTable={false}
              compact={true}
              center={center}
              onCenterChange={setCenter}
              yearsSpan={yearsSpan}
              onYearsSpanChange={setYearsSpan}
              hideControls={true}
            />
          </Box>

          {/* MD Theme Card */}
          {dashaCtxUsed && (() => {
            const mdLevel = dashaCtxUsed.levels.find(l => l.level === 'MD');
            if (!mdLevel) return null;
            // Get functional type for color
            const funcType = functionalClassification?.[mdLevel.planet] || 'neutral';
            const planetColor = funcType === 'benefic' ? 'var(--joy-palette-success-700)' : funcType === 'malefic' ? 'var(--joy-palette-danger-700)' : '#666';
            // Cosmetic helpers
            const houseNames = [
              'Self and Personality', 'Money, Family and Possessions', 'Siblings and Communication',
              'Mother and Home', 'Children, Creativity and Romance', 'Job, Health and Service',
              'Marriage and Partnerships', 'Transformation, Mysteries and Hidden Knowledge',
              'Fortune, Luck and Father', 'Career and Status', 'Gains, Income and Friends',
              'Troubles, Losses and Spiritual Awakening'
            ];
            const houseIdx = (mdLevel.occupies ?? 1) - 1;
            const spotlight = houseNames[houseIdx] || '';
            const houseLabel = `House of ${spotlight}`;
            // Duration in years/months
            const fromDt = DateTime.fromISO(mdLevel.from);
            const toDt = DateTime.fromISO(mdLevel.to);
            let durationStr = '';
            if (fromDt.isValid && toDt.isValid) {
              const years = toDt.diff(fromDt, 'years').years;
              const months = toDt.diff(fromDt, 'months').months % 12;
              durationStr = `${Math.floor(years)}y${months > 0 ? ' ' + Math.round(months) + 'm' : ''}`;
            }
            // Full date/time for subheading
            const mdDuration = `${fmtHuman(mdLevel.from)} — ${fmtHuman(mdLevel.to)}`;
            // Get all life areas activated by MD
            const mdHouses = [...mdLevel.rules, mdLevel.occupies, ...(mdLevel.aspects || [])];
            const uniqueHouses = Array.from(new Set(mdHouses)).sort((a, b) => a - b);
            const mdLifeAreas = new Set<string>();
            uniqueHouses.forEach(h => {
              const areas = (HOUSE_TO_LIFE as any)[h] || [];
              areas.forEach((a: string) => mdLifeAreas.add(a));
            });
            // Calculate theme score
            const { baseImpact, transitPoints } = calculateThemeScore(mdLevel, transitCtx);
            // Get real nakshatra/zodiac for dasha planet (Moon for Rahu/Ketu)
            let nakshatraName = '';
            let zodiacName = '';
            try {
              // Use Moon for Rahu/Ketu, else planet
              const planet = mdLevel.planet === 'Rahu' || mdLevel.planet === 'Ketu' ? 'Moon' : mdLevel.planet;
              const grahas = getGrahaLongitudesAndMotionSidereal({ isoDate: birthDate, latitude: location.lat, longitude: location.lon }, ayanamsaProvider);
              const planetLon = grahas?.[planet]?.lon;
              if (planetLon != null) {
                // Compute nakshatra and zodiac from longitude
                const nakIndex0 = Math.floor(planetLon / (360 / 27)); // 0..26
                const rashiIndex0 = Math.floor(planetLon / 30);       // 0..11
                nakshatraName = NAKSHATRA_NAMES[nakIndex0] || '';
                zodiacName = RASHI_NAMES[rashiIndex0] || '';
              }
            } catch {}
            return (
              <Card variant="outlined" color="primary" sx={{ p: 1.2, borderRadius: 3, boxShadow: 'md' }}>
                <Typography level="title-lg" sx={{ mb: 0.2, fontWeight: 600, lineHeight: 1.3, fontSize: '1.1rem' }}>
                  Maha Dasha (Your Life Theme) ({durationStr}) - <span style={{ fontWeight: 700, color: planetColor }}>{mdLevel.planet}</span> of <span style={{ color: '#b8860b' }}>{nakshatraName}</span> Nakshatra sitting in <span style={{ color: '#1976d2' }}>{zodiacName}</span>
                </Typography>
                <Typography level="body-md" sx={{ mb: 0.2, fontWeight: 500, lineHeight: 1.2 }}>
                  <strong>Spotlight:</strong> {houseLabel}
                </Typography>
                <Typography level="body-xs" sx={{ mb: 0.2, opacity: 0.7, lineHeight: 1.2 }}>
                  {mdDuration}
                </Typography>
                <Typography level="body-xs" sx={{ mb: 0.2, lineHeight: 1.2 }}>
                  Occupies <strong>{houseLabel}</strong>, rules <strong>{mdLevel.rules.length > 0 ? `H${mdLevel.rules.join(', H')}` : 'None'}</strong>, aspects <strong>{mdLevel.aspects && mdLevel.aspects.length > 0 ? `H${mdLevel.aspects.join(', H')}` : 'None'}</strong>
                </Typography>
                <Typography level="body-sm" sx={{ mb: 0.2, fontWeight: 600, lineHeight: 1.2 }}>
                  Smart Transit with Overall Benefic and Malefic Influence
                </Typography>
                {mdTransitStats.loading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <CircularProgress size="sm" />
                    <Typography level="body-xs">Analyzing transits...</Typography>
                  </Box>
                ) : mdTransitStats.stats ? (
                  <Box sx={{ mb: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.2 }}>
                      <Typography level="body-xs" sx={{ fontWeight: 600 }}>Transit Analysis:</Typography>
                      <Tooltip 
                        title={
                          <Box sx={{ p: 0.5 }}>
                            <Typography level="body-sm" sx={{ fontWeight: 600, mb: 0.5 }}>Transit Metrics Explained:</Typography>
                            <Typography level="body-xs" sx={{ mb: 0.5 }}>
                              <strong>Benefic Support:</strong> Strength of beneficial planetary influences (0-100%)
                            </Typography>
                            <Typography level="body-xs" sx={{ mb: 0.5 }}>
                              <strong>Malefic Pressure:</strong> Intensity of challenging planetary influences (0-100%)
                            </Typography>
                            <Typography level="body-xs" sx={{ mb: 0.5 }}>
                              <strong>Transit Support:</strong> Average number of slow planets in focus areas
                            </Typography>
                            <Typography level="body-xs" sx={{ fontWeight: 600, mt: 1, mb: 0.5 }}>Activity Levels:</Typography>
                            <Typography level="body-xs">• <strong>Low (1-2):</strong> Quiet period, minimal planetary attention</Typography>
                            <Typography level="body-xs">• <strong>Medium (3-4):</strong> Moderate activity, balanced energy</Typography>
                            <Typography level="body-xs">• <strong>High (5+):</strong> Intense focus, multiple events likely</Typography>
                          </Box>
                        }
                        placement="right"
                        variant="outlined"
                      >
                        <IconButton size="sm" variant="plain" sx={{ minHeight: 0, minWidth: 0, p: 0.25 }}>
                          <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      <Chip size="sm" variant="soft" color="success">
                        ✓ {mdTransitStats.stats.jupiterBlessing ?? 0}% benefic support
                      </Chip>
                      <Chip size="sm" variant="soft" color="warning">
                        ⚠ {mdTransitStats.stats.saturnPressure ?? 0}% malefic pressure
                      </Chip>
                      <Chip 
                        size="sm" 
                        variant="soft" 
                        color={
                          mdTransitStats.stats.avgTransits >= 5 ? 'danger' :
                          mdTransitStats.stats.avgTransits >= 3 ? 'primary' : 'neutral'
                        }
                      >
                        {mdTransitStats.stats.avgTransits >= 5 ? '🔥' : mdTransitStats.stats.avgTransits >= 3 ? '⚡' : '○'} Transit Support {mdTransitStats.stats.avgTransits ?? 0}
                      </Chip>
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
                    <Chip size="sm" variant="soft" color={baseImpact >= 0 ? 'success' : 'danger'}>
                      Base: {baseImpact > 0 ? '+' : ''}{baseImpact}
                    </Chip>
                    <Chip size="sm" variant="soft" color={transitPoints >= 0 ? 'success' : 'warning'}>
                      Transit: {transitPoints > 0 ? '+' : ''}{transitPoints}
                    </Chip>
                  </Box>
                )}
                <Typography level="body-sm" sx={{ fontWeight: 600, mb: 0.2, lineHeight: 1.2 }}>
                  Life Focus Areas:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {Array.from(mdLifeAreas).slice(0, 6).map(area => (
                    <Chip key={area as string} size="sm" variant="soft" color="primary">
                      {area}
                    </Chip>
                  ))}
                  {mdLifeAreas.size > 6 && (
                    <Chip size="sm" variant="outlined" color="neutral">
                      +{mdLifeAreas.size - 6} more
                    </Chip>
                  )}
                </Box>
              </Card>
            );
          })()}

          {/* AD Theme Card */}
          {dashaCtxUsed && (() => {
            const adLevel = dashaCtxUsed.levels.find(l => l.level === 'AD');
            if (!adLevel) return null;
            // Get functional type for color
            const funcType = functionalClassification?.[adLevel.planet] || 'neutral';
            const planetColor = funcType === 'benefic' ? 'var(--joy-palette-success-700)' : funcType === 'malefic' ? 'var(--joy-palette-danger-700)' : '#666';
            // Cosmetic helpers
            const houseNames = [
              'Self and Personality', 'Money, Family and Possessions', 'Siblings and Communication',
              'Mother and Home', 'Children, Creativity and Romance', 'Job, Health and Service',
              'Marriage and Partnerships', 'Transformation, Mysteries and Hidden Knowledge',
              'Fortune, Luck and Father', 'Career and Status', 'Gains, Income and Friends',
              'Troubles, Losses and Spiritual Awakening'
            ];
            const houseIdx = (adLevel.occupies ?? 1) - 1;
            const spotlight = houseNames[houseIdx] || '';
            const houseLabel = `House of ${spotlight}`;
            // Duration in years/months
            const fromDt = DateTime.fromISO(adLevel.from);
            const toDt = DateTime.fromISO(adLevel.to);
            let durationStr = '';
            if (fromDt.isValid && toDt.isValid) {
              const years = toDt.diff(fromDt, 'years').years;
              const months = toDt.diff(fromDt, 'months').months % 12;
              durationStr = `${Math.floor(years)}y${months > 0 ? ' ' + Math.round(months) + 'm' : ''}`;
            }
            // Full date/time for subheading
            const adDuration = `${fmtHuman(adLevel.from)} — ${fmtHuman(adLevel.to)}`;
            // Get all life areas activated by AD
            const adHouses = [...adLevel.rules, adLevel.occupies, ...(adLevel.aspects || [])];
            const uniqueHouses = Array.from(new Set(adHouses)).sort((a, b) => a - b);
            const adLifeAreas = new Set<string>();
            uniqueHouses.forEach(h => {
              const areas = (HOUSE_TO_LIFE as any)[h] || [];
              areas.forEach((a: string) => adLifeAreas.add(a));
            });
            // Calculate theme score
            const { baseImpact, transitPoints } = calculateThemeScore(adLevel, transitCtx);
            // Get real nakshatra/zodiac for dasha planet (Moon for Rahu/Ketu)
            let nakshatraName = '';
            let zodiacName = '';
            try {
              // Use Moon for Rahu/Ketu, else planet
              const planet = adLevel.planet === 'Rahu' || adLevel.planet === 'Ketu' ? 'Moon' : adLevel.planet;
              const grahas = getGrahaLongitudesAndMotionSidereal({ isoDate: birthDate, latitude: location.lat, longitude: location.lon }, ayanamsaProvider);
              const planetLon = grahas?.[planet]?.lon;
              if (planetLon != null) {
                const nakIndex0 = Math.floor(planetLon / (360 / 27)); // 0..26
                const rashiIndex0 = Math.floor(planetLon / 30);       // 0..11
                nakshatraName = NAKSHATRA_NAMES[nakIndex0] || '';
                zodiacName = RASHI_NAMES[rashiIndex0] || '';
              }
            } catch {}
            return (
              <Card variant="outlined" color="neutral" sx={{ p: 1.2, borderRadius: 3, boxShadow: 'md' }}>
                <Typography level="title-lg" sx={{ mb: 0.2, fontWeight: 600, lineHeight: 1.3, fontSize: '1.1rem' }}>
                  Antar Dasha (Medium-Term Theme) ({durationStr}) - <span style={{ fontWeight: 700, color: planetColor }}>{adLevel.planet}</span> of <span style={{ color: '#b8860b' }}>{nakshatraName}</span> Nakshatra sitting in <span style={{ color: '#1976d2' }}>{zodiacName}</span>
                </Typography>
                <Typography level="body-md" sx={{ mb: 0.2, fontWeight: 500, lineHeight: 1.2 }}>
                  <strong>Spotlight:</strong> {houseLabel}
                </Typography>
                <Typography level="body-xs" sx={{ mb: 0.2, opacity: 0.7, lineHeight: 1.2 }}>
                  {adDuration}
                </Typography>
                <Typography level="body-xs" sx={{ mb: 0.2, lineHeight: 1.2 }}>
                  Occupies <strong>{houseLabel}</strong>, rules <strong>{adLevel.rules.length > 0 ? `H${adLevel.rules.join(', H')}` : 'None'}</strong>, aspects <strong>{adLevel.aspects && adLevel.aspects.length > 0 ? `H${adLevel.aspects.join(', H')}` : 'None'}</strong>
                </Typography>
                <Typography level="body-sm" sx={{ mb: 0.2, fontWeight: 600, lineHeight: 1.2 }}>
                  Smart Transit with Overall Benefic and Malefic Influence
                </Typography>
                {adTransitStats.loading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <CircularProgress size="sm" />
                    <Typography level="body-xs">Analyzing transits...</Typography>
                  </Box>
                ) : adTransitStats.stats ? (
                  <Box sx={{ mb: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.2 }}>
                      <Typography level="body-xs" sx={{ fontWeight: 600 }}>Transit Analysis:</Typography>
                      <Tooltip 
                        title={
                          <Box sx={{ p: 0.5 }}>
                            <Typography level="body-sm" sx={{ fontWeight: 600, mb: 0.5 }}>Transit Metrics Explained:</Typography>
                            <Typography level="body-xs" sx={{ mb: 0.5 }}>
                              <strong>Benefic Support:</strong> Strength of beneficial planetary influences (0-100%)
                            </Typography>
                            <Typography level="body-xs" sx={{ mb: 0.5 }}>
                              <strong>Malefic Pressure:</strong> Intensity of challenging planetary influences (0-100%)
                            </Typography>
                            <Typography level="body-xs" sx={{ mb: 0.5 }}>
                              <strong>Transit Support:</strong> Average number of slow planets in focus areas
                            </Typography>
                            <Typography level="body-xs" sx={{ fontWeight: 600, mt: 1, mb: 0.5 }}>Activity Levels:</Typography>
                            <Typography level="body-xs">• <strong>Low (1-2):</strong> Quiet period, minimal planetary attention</Typography>
                            <Typography level="body-xs">• <strong>Medium (3-4):</strong> Moderate activity, balanced energy</Typography>
                            <Typography level="body-xs">• <strong>High (5+):</strong> Intense focus, multiple events likely</Typography>
                          </Box>
                        }
                        placement="right"
                        variant="outlined"
                      >
                        <IconButton size="sm" variant="plain" sx={{ minHeight: 0, minWidth: 0, p: 0.25 }}>
                          <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      <Chip size="sm" variant="soft" color="success">
                        ✓ {adTransitStats.stats.jupiterBlessing ?? 0}% benefic support
                      </Chip>
                      <Chip size="sm" variant="soft" color="warning">
                        ⚠ {adTransitStats.stats.saturnPressure ?? 0}% malefic pressure
                      </Chip>
                      <Chip 
                        size="sm" 
                        variant="soft" 
                        color={
                          adTransitStats.stats.avgTransits >= 5 ? 'danger' :
                          adTransitStats.stats.avgTransits >= 3 ? 'primary' : 'neutral'
                        }
                      >
                        {adTransitStats.stats.avgTransits >= 5 ? '🔥' : adTransitStats.stats.avgTransits >= 3 ? '⚡' : '○'} Transit Support {adTransitStats.stats.avgTransits ?? 0}
                      </Chip>
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
                    <Chip size="sm" variant="soft" color={baseImpact >= 0 ? 'success' : 'danger'}>
                      Base: {baseImpact > 0 ? '+' : ''}{baseImpact}
                    </Chip>
                    <Chip size="sm" variant="soft" color={transitPoints >= 0 ? 'success' : 'warning'}>
                      Transit: {transitPoints > 0 ? '+' : ''}{transitPoints}
                    </Chip>
                  </Box>
                )}
                <Typography level="body-sm" sx={{ fontWeight: 600, mb: 0.2, lineHeight: 1.2 }}>
                  Life Focus Areas:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {Array.from(adLifeAreas).slice(0, 6).map(area => (
                    <Chip key={area as string} size="sm" variant="soft" color="neutral">
                      {area}
                    </Chip>
                  ))}
                  {adLifeAreas.size > 6 && (
                    <Chip size="sm" variant="outlined" color="neutral">
                      +{adLifeAreas.size - 6} more
                    </Chip>
                  )}
                </Box>
              </Card>
            );
          })()}
        </Box>
      )}

      {/* Feeds Tabs - Permanent Features & Doshas */}
      <Tabs 
        value={activeTab} 
        onChange={(e, newValue) => setActiveTab(newValue as number)}
        sx={{ bgcolor: 'background.surface', borderRadius: 2, mt: 3 }}
      >
        <TabList 
          variant="outlined"
          sx={{ 
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            bgcolor: 'background.level1',
            borderRadius: 'lg',
            p: 0.5,
            gap: 1,
            '& .MuiTab-root': {
              fontSize: { xs: '0.875rem', sm: '0.9375rem' },
              padding: { xs: '8px 12px', sm: '12px 20px' },
              minHeight: { xs: 40, sm: 48 },
              borderRadius: 'md',
              transition: 'all 0.2s ease',
              color: 'text.primary',
              fontWeight: 500,
              '&:hover': {
                bgcolor: 'background.level2'
              },
              '&.Mui-selected': {
                bgcolor: 'primary.solidBg',
                color: 'primary.solidColor',
                fontWeight: 600
              }
            }
          }}>
          <Tab value={0}>
            🕉️ Permanent Features & Yogas
            {natalYogaFeeds.length > 0 && (
              <Chip size="sm" variant="soft" color="primary" sx={{ ml: 1 }}>
                {natalYogaFeeds.length}
              </Chip>
            )}
          </Tab>
          <Tab value={1}>
            📋 Doshas & Planetary Combinations
            {natalPredictionFeeds.length > 0 && (
              <Chip size="sm" variant="soft" color="success" sx={{ ml: 1 }}>
                {natalPredictionFeeds.length}
              </Chip>
            )}
          </Tab>
        </TabList>

        {/* Tab 0: Permanent natal features and yogas */}
        <TabPanel value={0} sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
          {natalYogaFeeds.length > 0 ? (
            <Box id="section-permanent-yogas">
              {/* Controls Row */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Switch
                    checked={showUniqueOnly}
                    onChange={(e) => setShowUniqueOnly(e.target.checked)}
                    size="sm"
                  />
                  <Typography level="body-sm" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Show unique yogas only</Typography>
                </Box>
                
                <Button
                  size="sm"
                  variant="soft"
                  startDecorator={<PrintIcon />}
                  onClick={handleExportPDF}
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                >
                  Export PDF
                </Button>
              </Box>
              
              <Box sx={{ mb: 3, p: 2, bgcolor: 'primary.softBg', borderRadius: 'md', borderLeft: '4px solid', borderColor: 'primary.solidBg' }}>
                <Typography level="body-sm" sx={{ color: 'primary.700', fontSize: { xs: '0.75rem', sm: '0.875rem' }, lineHeight: 1.6 }}>
                  These are <strong>permanent features</strong> of your birth chart—natal yogas and conditions that remain active throughout your life. 
                  Unlike time-sensitive predictions, these represent your core astrological blueprint and inherent life patterns.
                </Typography>
              </Box>
              <Stack spacing={3}>
                {Object.entries(groupedNatalYoga).map(([lifeArea, items]) => (
                  <Box key={lifeArea}>
                    <Typography level="h4" sx={{ mb: 2, color: 'primary.600', fontWeight: 600 }}>
                      {lifeArea}
                    </Typography>
                    <Stack spacing={1.5}>
                      {items.map((item) => {
                  const isYoga = item.ruleKey === 'kaal_sarp_yoga' || item.ruleKey?.includes('_natal') || item.ruleKey?.includes('yoga');
                  const isRajYoga = item.ruleKey?.includes('raj_yoga');
                  
                  // Extract yoga title - use rule key as fallback with proper formatting
                  const titleMatch = item.message.match(/^([^:]+):/);
                  let title = titleMatch ? titleMatch[1].trim() : '';
                  
                  // If title is generic (like "NATAL RAJ YOGA"), use the rule key for specific name
                  if (!title || (title.includes('NATAL RAJ YOGA') && !title.includes('Dharma') && !title.includes('Kendra'))) {
                    // Format rule key nicely: dharma_karma_raj_yoga_natal -> Dharma-Karma Raj Yoga
                    const formattedKey = item.ruleKey
                      ? item.ruleKey
                          .replace(/_natal$/, '')
                          .replace(/_/g, ' ')
                          .split(' ')
                          .map((word: string) => {
                            if (word === 'raj' || word === 'yoga') return word.charAt(0).toUpperCase() + word.slice(1);
                            if (word === 'dharma' || word === 'karma') return word.charAt(0).toUpperCase() + word.slice(1);
                            if (word === 'kendra' || word === 'trikona') return word.charAt(0).toUpperCase() + word.slice(1);
                            if (word === 'gaja' || word === 'kesari') return word.charAt(0).toUpperCase() + word.slice(1);
                            if (word === 'budhaditya' || word === 'malavya' || word === 'hamsa' || word === 'shasha' || word === 'bhadra' || word === 'ruchaka') return word.charAt(0).toUpperCase() + word.slice(1);
                            if (word === 'lakshmi' || word === 'vipareeta' || word === 'chandra' || word === 'mangala' || word === 'amala') return word.charAt(0).toUpperCase() + word.slice(1);
                            if (word === 'sunapha' || word === 'anapha' || word === 'durudhara' || word === 'parivartana') return word.charAt(0).toUpperCase() + word.slice(1);
                            if (word === 'adhi' || word === 'neech' || word === 'bhang') return word.charAt(0).toUpperCase() + word.slice(1);
                            return word;
                          })
                          .join(' ')
                      : 'Yoga';
                    title = formattedKey;
                  }
                  
                  // Remove date formatting from message body (permanent yogas don't need dates)
                  let body = titleMatch ? item.message.substring(titleMatch[0].length).trim() : item.message;
                  // Remove "between X and Y" date strings
                  body = body.replace(/\s*between\s+\d{1,2}(?:st|nd|rd|th)\s+\w{3}\s+\d{4}\s*\|\s*\d{1,2}:\d{2}\s*(?:am|pm)\s+and\s+\d{1,2}(?:st|nd|rd|th)\s+\w{3}\s+\d{4}\s*\|\s*\d{1,2}:\d{2}\s*(?:am|pm)\s*\.?/gi, '');
                  
                  // Extract description - prioritize PERMANENT BLESSING, then EFFECTS, then NATURE sections
                  // Match multiple sentences for richer content (up to 3 sentences or 300 chars)
                  let description = '';
                  
                  // Try PERMANENT BLESSING first (most relevant for natal features)
                  const permanentMatch = body.match(/PERMANENT BLESSING:\s*([^]+?)(?:EFFECTS:|MANIFESTATION:|ACTIVATION:|CHARACTER:|NATURE:|CLASSICAL|PARASHARA|UTILIZE:|$)/i);
                  if (permanentMatch && permanentMatch[1]) {
                    description = permanentMatch[1].trim();
                  } else {
                    // Try EFFECTS section
                    const effectsMatch = body.match(/EFFECTS:\s*([^]+?)(?:MANIFESTATION:|ACTIVATION:|CHARACTER:|PERMANENT|CLASSICAL|PARASHARA|UTILIZE:|$)/i);
                    if (effectsMatch && effectsMatch[1]) {
                      description = effectsMatch[1].trim();
                    } else {
                      // Try NATURE section
                      const natureMatch = body.match(/NATURE:\s*([^]+?)(?:EFFECTS:|MANIFESTATION:|ACTIVATION:|CHARACTER:|PERMANENT|CLASSICAL|PARASHARA|UTILIZE:|$)/i);
                      if (natureMatch && natureMatch[1]) {
                        description = natureMatch[1].trim();
                      } else {
                        // Fallback: Take first 2-3 sentences or 300 characters
                        const sentences = body.match(/[^.!?]+[.!?]+/g) || [];
                        description = sentences.slice(0, 3).join(' ').trim() || body.substring(0, 300).trim() + '...';
                      }
                    }
                  }
                  
                  // Clean up description: limit to ~600 chars for readability
                  if (description.length > 600) {
                    const truncated = description.substring(0, 600);
                    const lastPeriod = truncated.lastIndexOf('.');
                    description = lastPeriod > 300 ? truncated.substring(0, lastPeriod + 1) : truncated + '...';
                  }
                  
                  return (
                    <Card
                      key={item.id}
                      id={item.id}
                      data-feed-id={item.id}
                      variant="outlined"
                      sx={{
                        borderLeft: `4px solid var(--joy-palette-${severityColor(item.severity)}-500)`,
                        bgcolor: 'background.level1',
                        boxShadow: isRajYoga ? 'md' : 'sm',
                        transition: 'all 0.2s',
                        '&:hover': { boxShadow: 'lg', transform: 'translateY(-2px)' }
                      }}
                    >
                      <Box>
                        {/* Header with badges */}
                        <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                          {item.private && (
                            <Chip size="sm" variant="solid" color="danger" sx={{ fontWeight: 'bold' }}>
                              🔒 ADMIN ONLY
                            </Chip>
                          )}
                          {isYoga && (
                            <Chip size="sm" variant="solid" color="success">
                              🕉️ YOGA
                            </Chip>
                          )}
                          {isRajYoga && (
                            <Chip size="sm" variant="solid" color="warning" sx={{ fontWeight: 'bold' }}>
                              👑 RAJ YOGA
                            </Chip>
                          )}
                          <Chip size="sm" variant="soft" color={severityColor(item.severity) as any}>
                            {severityLabel(item.severity)}
                          </Chip>
                          <Chip size="sm" variant="outlined">
                            {item.score.toFixed(0)} pts
                          </Chip>
                        </Box>
                        
                        {/* Title */}
                        <Typography level="title-md" sx={{ mb: 0.5, fontWeight: 600, color: isRajYoga ? 'warning.600' : 'primary.700' }}>
                          {title}
                        </Typography>
                        
                        {/* Concise description */}
                        <Typography level="body-sm" sx={{ mb: 1, color: 'neutral.700', lineHeight: 1.5 }}>
                          {description}
                        </Typography>
                        
                        {/* Evidence - collapsed by default, shown on expand */}
                        {item.evidence && item.evidence.length > 0 && (
                          <details>
                            <summary style={{ cursor: 'pointer', fontSize: '0.75rem', color: 'var(--joy-palette-neutral-500)', marginTop: '4px' }}>
                              📋 View Details
                            </summary>
                            <Typography level="body-xs" sx={{ mt: 1, p: 1, bgcolor: 'background.level2', borderRadius: 'sm', opacity: 0.8 }}>
                              {item.evidence
                                .filter((e: string) => {
                                  const lower = e.toLowerCase();
                                  return !lower.includes('between') && 
                                         !lower.includes('natal raj yogas detected') &&
                                         !lower.includes('permanent blessing') &&
                                         !lower.includes('activation:') &&
                                         !lower.includes('manifestation:') &&
                                         !/\d{1,2}(?:st|nd|rd|th)\s+\w{3}\s+\d{4}/.test(e); // Filter any date patterns
                                })
                                .join(' • ')}
                            </Typography>
                          </details>
                        )}
                      </Box>
                    </Card>
                  );
                })}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </Box>
          ) : (
            <Box sx={{ p: 3, textAlign: 'center', borderRadius: 'md', border: '1px dashed var(--joy-palette-neutral-outlinedBorder)', bgcolor: 'neutral.softBg' }}>
              <Typography level="body-lg" color="neutral">
                No permanent features detected
              </Typography>
            </Box>
          )}

        {/* Tab 1: Active Yogas & Predictions (hidden) */}
        <Box sx={{ display: 'none' }}>
          {timingFeeds.length > 0 ? (
            <Box>
              {/* Controls Row */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Switch
                    checked={showUniqueActiveOnly}
                    onChange={(e) => setShowUniqueActiveOnly(e.target.checked)}
                    size="sm"
                  />
                  <Typography level="body-sm" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Show unique yogas only</Typography>
                </Box>
                
                <Button
                  size="sm"
                  variant="soft"
                  startDecorator={<PrintIcon />}
                  onClick={handleExportPDF}
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                >
                  Export PDF
                </Button>
              </Box>
              
              <Typography level="body-sm" sx={{ mb: 2, color: 'neutral.600', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                These predictions are triggered by the current dasha period (PD/SD level) and activated yogas. 
                They indicate time-sensitive opportunities and challenges for this specific window.
              </Typography>
              <Stack spacing={3}>
                {Object.entries(groupedTiming).map(([lifeArea, items]) => (
                  <Box key={lifeArea}>
                    <Typography level="h4" sx={{ mb: 1.5, textTransform: 'capitalize', color: 'warning.600' }}>
                      {lifeArea.replace(/_/g, ' ')}
                    </Typography>
                    <Stack spacing={1.5}>
                      {items.map((item) => {
                        const isSadeSati = item.ruleKey?.startsWith('sade_sati');
                        const isActivatedYoga = item.ruleKey?.includes('yoga') && !isSadeSati;
                        
                        // Extract transit support from evidence field
                        let transitSupport: number | null = null;
                        if (item.evidence && item.evidence.length > 0) {
                          const transitEvidence = item.evidence.find(e => e.includes('TransitP='));
                          if (transitEvidence) {
                            const match = transitEvidence.match(/TransitP=([-\d.]+)/);
                            if (match) {
                              transitSupport = parseFloat(match[1]);
                            }
                          }
                        }
                        
                        return (
                          <Card
                            key={item.id}
                            id={item.id}
                            data-feed-id={item.id}
                            variant="outlined"
                            sx={{
                              borderLeft: `4px solid var(--joy-palette-${severityColor(item.severity)}-500)`,
                              boxShadow: isActivatedYoga ? 'lg' : 'sm',
                              scrollMarginTop: '100px' // Offset for sticky headers
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                              <Box sx={{ flex: 1 }}>
                                <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                                  {item.private && (
                                    <Chip size="sm" variant="solid" color="danger" sx={{ fontWeight: 'bold' }}>
                                      🔒 ADMIN ONLY
                                    </Chip>
                                  )}
                                  {isSadeSati && (
                                    <Chip size="sm" variant="solid" color="warning">
                                      🪐 SADE SATI
                                    </Chip>
                                  )}
                                  {isActivatedYoga && (
                                    <Chip size="sm" variant="solid" color="success">
                                      ⚡ YOGA ACTIVATED
                                    </Chip>
                                  )}
                                  <Chip size="sm" color={severityColor(item.severity) as any}>
                                    {severityLabel(item.severity)}
                                  </Chip>
                                  <Chip size="sm" variant="soft">
                                    Score: {item.score.toFixed(1)}
                                  </Chip>
                                  {transitSupport !== null && (
                                    <Chip 
                                      size="sm" 
                                      variant="soft" 
                                      color={transitSupport >= 10 ? 'success' : transitSupport <= -10 ? 'danger' : 'neutral'}
                                    >
                                      Transit Support: {transitSupport > 0 ? '+' : ''}{transitSupport.toFixed(1)}
                                    </Chip>
                                  )}
                                  {item.highProbability && (
                                    <Chip size="sm" variant="solid" color="danger">
                                      ⚡ High Probability
                                    </Chip>
                                  )}
                                </Box>
                                <Typography level="body-md" sx={{ mt: 1 }}>
                                  {item.message}
                                </Typography>
                                {item.evidence && item.evidence.length > 0 && (
                                  <Typography level="body-xs" sx={{ mt: 1, opacity: 0.7 }}>
                                    Evidence: {item.evidence.join('; ')}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </Card>
                        );
                      })}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </Box>
          ) : (
            hasMDAThemes ? (
              <Box sx={{ p: 3, textAlign: 'center', borderRadius: 'md', border: '1px dashed var(--joy-palette-neutral-outlinedBorder)', bgcolor: 'neutral.softBg' }}>
                <Typography level="body-lg" color="neutral">
                  No immediate events detected for current period
                </Typography>
                <Typography level="body-sm" color="neutral" sx={{ mt: 0.5 }}>
                  Review MD/AD themes above for long-term planetary influences
                </Typography>
              </Box>
            ) : (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography level="body-lg" color="neutral">
                  No significant events detected
                </Typography>
              </Box>
            )
          )}
        </Box>
        </TabPanel>

        {/* Tab 1: Doshas & Planetary Combinations */}
        <TabPanel value={1} sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
          {/* DOSHAS SECTION */}
          <Box id="section-doshas" sx={{ mb: 4 }}>
            {natalYogaFeeds.some(f => f.ruleKey === 'kaal_sarp_yoga') || natalPredictionFeeds.some(f => f.ruleKey === 'mangal_dosha_natal') ? (
              <Stack spacing={2}>
                {/* Kaal Sarp Yoga */}
                {natalYogaFeeds
                  .filter(f => f.ruleKey === 'kaal_sarp_yoga')
                  .map(item => {
                    const titleMatch = item.message.match(/^([^:]+):/);
                    const title = titleMatch ? titleMatch[1].trim() : 'Kaal Sarp Yoga';
                    let body = titleMatch ? item.message.substring(titleMatch[0].length).trim() : item.message;
                    
                    // Extract description from NATURE or EFFECTS section
                    let description = '';
                    const natureMatch = body.match(/NATURE:\s*([^]+?)(?:EFFECTS:|MANIFESTATION:|REMEDIES:|$)/i);
                    if (natureMatch && natureMatch[1]) {
                      description = natureMatch[1].trim();
                    } else {
                      const effectsMatch = body.match(/EFFECTS:\s*([^]+?)(?:MANIFESTATION:|REMEDIES:|$)/i);
                      if (effectsMatch && effectsMatch[1]) {
                        description = effectsMatch[1].trim();
                      } else {
                        const sentences = body.match(/[^.!?]+[.!?]+/g) || [];
                        description = sentences.slice(0, 2).join(' ').trim() || body.substring(0, 300).trim() + '...';
                      }
                    }
                    
                    // Truncate description to ~300 chars
                    if (description.length > 300) {
                      const truncated = description.substring(0, 300);
                      const lastPeriod = truncated.lastIndexOf('.');
                      description = lastPeriod > 150 ? truncated.substring(0, lastPeriod + 1) : truncated + '...';
                    }
                    
                    return (
                      <Card
                        key={item.id}
                        variant="outlined"
                        sx={{
                          borderLeft: `4px solid var(--joy-palette-warning-500)`,
                          bgcolor: 'background.level1',
                          boxShadow: 'sm',
                          transition: 'all 0.2s',
                          '&:hover': { boxShadow: 'lg', transform: 'translateY(-2px)' }
                        }}
                      >
                        <Box>
                          {/* Header with badges */}
                          <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                            <Chip size="sm" variant="solid" color="warning">
                              ⚠️ DOSHA
                            </Chip>
                            <Chip size="sm" variant="soft" color={severityColor(item.severity) as any}>
                              {severityLabel(item.severity)}
                            </Chip>
                            <Chip size="sm" variant="outlined">
                              {item.score.toFixed(0)} pts
                            </Chip>
                          </Box>
                          
                          {/* Title */}
                          <Typography level="title-md" sx={{ mb: 0.5, fontWeight: 600, color: 'warning.700' }}>
                            {title}
                          </Typography>
                          
                          {/* Concise description */}
                          <Typography level="body-sm" sx={{ mb: 1, color: 'neutral.700', lineHeight: 1.5 }}>
                            {description}
                          </Typography>
                          
                          {/* Full message and evidence - collapsed by default */}
                          <details>
                            <summary style={{ cursor: 'pointer', fontSize: '0.75rem', color: 'var(--joy-palette-neutral-500)', marginTop: '4px' }}>
                              📋 View Details
                            </summary>
                            <Box sx={{ mt: 1, p: 1.5, bgcolor: 'background.level2', borderRadius: 'sm' }}>
                              <Typography level="body-sm" sx={{ mb: 1.5, whiteSpace: 'pre-line', lineHeight: 1.6 }}>
                                {body}
                              </Typography>
                              {item.evidence && item.evidence.length > 0 && (
                                <Box sx={{ mt: 1 }}>
                                  <Typography level="body-xs" sx={{ fontWeight: 600, mb: 0.5, color: 'neutral.600' }}>
                                    📊 Chart Analysis:
                                  </Typography>
                                  <Typography level="body-xs" sx={{ opacity: 0.8 }}>
                                    {item.evidence.join(' • ')}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </details>
                        </Box>
                      </Card>
                    );
                  })}
                
                {/* Mangal Dosha */}
                {natalPredictionFeeds
                  .filter(f => f.ruleKey === 'mangal_dosha_natal')
                  .map(item => {
                    // The title is the first line
                    const lines = item.message.split('\n').map(l => l.trim()).filter(l => l);
                    const title = (lines[0] || 'Mangal Dosha (Kuja Dosha)').replace(/^🔥\s*/, '').replace(/:$/, '');
                    
                    // Extract description: house-specific effect (lines with "HOUSE:")
                    let description = '';
                    const houseEffect = lines.find(l => l.includes('HOUSE:'));
                    if (houseEffect) {
                      // Extract just the effect part after "HOUSE:"
                      description = houseEffect.replace(/^•\s*\d+(ST|ND|RD|TH)\s+HOUSE:\s*/i, '').trim();
                    } else {
                      // Fallback: use second line or first meaningful line
                      description = lines[1] || lines[0] || '';
                    }
                    
                    // Truncate description to ~300 chars
                    if (description.length > 300) {
                      const truncated = description.substring(0, 300);
                      const lastPeriod = truncated.lastIndexOf('.');
                      description = lastPeriod > 150 ? truncated.substring(0, lastPeriod + 1) : truncated + '...';
                    }
                    
                    // Full message body for details section
                    const body = item.message;
                    
                    return (
                      <Card
                        key={item.id}
                        variant="outlined"
                        sx={{
                          borderLeft: `4px solid var(--joy-palette-danger-500)`,
                          bgcolor: 'background.level1',
                          boxShadow: 'sm',
                          transition: 'all 0.2s',
                          '&:hover': { boxShadow: 'lg', transform: 'translateY(-2px)' }
                        }}
                      >
                        <Box>
                          {/* Header with badges */}
                          <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                            <Chip size="sm" variant="solid" color="danger">
                              🔥 DOSHA
                            </Chip>
                            <Chip size="sm" variant="soft" color={severityColor(item.severity) as any}>
                              {severityLabel(item.severity)}
                            </Chip>
                            <Chip size="sm" variant="outlined">
                              {item.score.toFixed(0)} pts
                            </Chip>
                            <Chip 
                              size="sm"
                              variant="soft"
                              color={Math.abs(item.score) > 50 ? 'danger' : 'warning'}
                            >
                              {Math.abs(item.score) > 50 ? 'Triple/Double' : 'Single'}
                            </Chip>
                          </Box>
                          
                          {/* Title */}
                          <Typography level="title-md" sx={{ mb: 0.5, fontWeight: 600, color: 'danger.700' }}>
                            🔥 {title}
                          </Typography>
                          
                          {/* Concise description */}
                          <Typography level="body-sm" sx={{ mb: 1, color: 'neutral.700', lineHeight: 1.5 }}>
                            {description}
                          </Typography>
                          
                          {/* Full message and evidence - collapsed by default */}
                          <details>
                            <summary style={{ cursor: 'pointer', fontSize: '0.75rem', color: 'var(--joy-palette-neutral-500)', marginTop: '4px' }}>
                              📋 View Details
                            </summary>
                            <Box sx={{ mt: 1, p: 1.5, bgcolor: 'background.level2', borderRadius: 'sm' }}>
                              <Typography level="body-sm" sx={{ mb: 1.5, whiteSpace: 'pre-line', lineHeight: 1.6 }}>
                                {body}
                              </Typography>
                              {item.evidence && item.evidence.length > 0 && (
                                <Box sx={{ mt: 1 }}>
                                  <Typography level="body-xs" sx={{ fontWeight: 600, mb: 0.5, color: 'neutral.600' }}>
                                    📊 Chart Analysis:
                                  </Typography>
                                  <Typography level="body-xs" sx={{ opacity: 0.8 }}>
                                    {item.evidence.join(' • ')}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </details>
                        </Box>
                      </Card>
                    );
                  })}
              </Stack>
            ) : (
              <Card
                variant="soft"
                color="success"
                sx={{
                  p: 3,
                  textAlign: 'center',
                  borderLeft: '4px solid',
                  borderColor: 'success.solidBg'
                }}
              >
                <Typography level="h4" sx={{ color: 'success.700', mb: 1 }}>
                  ✅ No Major Doshas Detected
                </Typography>
                <Typography level="body-md" color="success" sx={{ opacity: 0.9 }}>
                  Your chart is free from Kaal Sarp Yoga, Mangal Dosha and similar major afflictions.
                </Typography>
                <Typography level="body-sm" color="success" sx={{ mt: 1, opacity: 0.7 }}>
                  This is a blessing indicating fewer karmic obstacles in your life path.
                </Typography>
              </Card>
            )}
          </Box>

          {/* PLANETARY COMBINATIONS SECTION */}
          {natalPredictionFeeds.length > 0 ? (
            <Box id="section-planetary-combinations">
              <Box sx={{ mb: 3, p: 2, bgcolor: 'success.softBg', borderRadius: 'md', borderLeft: '4px solid', borderColor: 'success.solidBg' }}>
                <Typography level="body-sm" sx={{ color: 'success.700', fontSize: { xs: '0.75rem', sm: '0.875rem' }, lineHeight: 1.6 }}>
                  These are <strong>natal predictions</strong> based on specific planet combinations in your birth chart. 
                  They represent permanent conditions that influence specific life areas throughout your life.
                </Typography>
              </Box>
              <Stack spacing={3}>
                {Object.entries(groupedNatalPredictions).map(([lifeArea, items]) => (
                  <Box key={lifeArea}>
                    <Typography level="h4" sx={{ mb: 2, color: 'primary.600', fontWeight: 600 }}>
                      {lifeArea}
                    </Typography>
                    <Stack spacing={1.5}>
                      {items.map((item) => {
                        // Extract title and description
                        let titleMatch = item.message.match(/^([^:]+?)(?:\s+between\s+\d|\s+\d{1,2}(?:st|nd|rd|th))?:/);
                        if (!titleMatch) {
                          titleMatch = item.message.match(/^([^:]+):/);
                        }
                        const title = titleMatch ? titleMatch[1].trim().replace(/\s+between\s+.*$/, '') : 'Planetary Combination';
                        let body = titleMatch ? item.message.substring(titleMatch[0].length).trim() : item.message;
                        
                        // Remove date patterns from start of body
                        body = body.replace(/^\s*between\s+\d{1,2}(?:st|nd|rd|th)\s+\w{3}\s+\d{4}\s*\|\s*\d{1,2}:\d{2}\s*(?:am|pm)\s+and\s+\d{1,2}(?:st|nd|rd|th)\s+\w{3}\s+\d{4}\s*\|\s*\d{1,2}:\d{2}\s*(?:am|pm)\.\s*/i, '');
                        body = body.replace(/^\s*\d{1,2}(?:st|nd|rd|th)?\s+\w{3}\s+\d{4}\s*\|\s*\d{1,2}:\d{2}\s*(?:am|pm).*?\.\s*/i, '');
                        // Remove any remaining date patterns throughout the body (including partial matches)
                        body = body.replace(/between\s+\d{1,2}(?:st|nd|rd|th)\s+\w{3,9}\s+\d{4}[^.]*?and\s+\d{1,2}(?:st|nd|rd|th)\s+\w{3,9}\s+\d{4}[^.]*?\./gi, '');
                        body = body.replace(/\d{1,2}(?:st|nd|rd|th)?\s+\w{3,9}\s+\d{4}\s*\|\s*\d{1,2}:\d{2}\s*(?:am|pm)[^.]*?\./gi, '');
                        body = body.replace(/^\s*\d{2}\s+am\s+and.*?\d{1,2}:\d{2}\s*(?:am|pm)\.\s*/gi, '');
                        body = body.trim();
                        
                        // Extract description from EFFECTS or first sentences
                        let description = '';
                        const effectsMatch = body.match(/EFFECTS:\s*([^]+?)(?:REMEDIES:|UTILIZE:|MANIFESTATION:|$)/i);
                        if (effectsMatch && effectsMatch[1]) {
                          description = effectsMatch[1].trim();
                        } else {
                          const natureMatch = body.match(/NATURE:\s*([^]+?)(?:EFFECTS:|REMEDIES:|$)/i);
                          if (natureMatch && natureMatch[1]) {
                            description = natureMatch[1].trim();
                          } else {
                            // Extract first meaningful sentence, skipping date patterns
                            const sentences = body.match(/[^.!?]+[.!?]+/g) || [];
                            const meaningfulSentences = sentences.filter(s => 
                              !s.match(/\d{1,2}(?:st|nd|rd|th)\s+\w{3}\s+\d{4}/) && 
                              s.trim().length > 20
                            );
                            description = meaningfulSentences.slice(0, 2).join(' ').trim() || body.substring(0, 300).trim() + '...';
                          }
                        }
                        
                        // Truncate description to ~300 chars
                        if (description.length > 300) {
                          const truncated = description.substring(0, 300);
                          const lastPeriod = truncated.lastIndexOf('.');
                          description = lastPeriod > 150 ? truncated.substring(0, lastPeriod + 1) : truncated + '...';
                        }
                        
                        return (
                          <Card
                            key={item.id}
                            variant="outlined"
                            sx={{
                              borderLeft: `4px solid var(--joy-palette-${severityColor(item.severity)}-500)`,
                              bgcolor: 'background.level1',
                              boxShadow: 'sm',
                              transition: 'all 0.2s',
                              '&:hover': { boxShadow: 'lg', transform: 'translateY(-2px)' }
                            }}
                          >
                            <Box>
                              {/* Header with badges */}
                              <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                                {item.private && (
                                  <Chip size="sm" variant="solid" color="danger" sx={{ fontWeight: 'bold' }}>
                                    🔒 ADMIN ONLY
                                  </Chip>
                                )}
                                <Chip size="sm" variant="solid" color="neutral">
                                  🔮 COMBINATION
                                </Chip>
                                <Chip size="sm" variant="soft" color={severityColor(item.severity) as any}>
                                  {severityLabel(item.severity)}
                                </Chip>
                                <Chip size="sm" variant="outlined">
                                  {item.score.toFixed(0)} pts
                                </Chip>
                              </Box>
                              
                              {/* Title */}
                              <Typography level="title-md" sx={{ mb: 0.5, fontWeight: 600, color: item.score >= 0 ? 'success.700' : 'danger.700' }}>
                                {title}
                              </Typography>
                              
                              {/* Concise description */}
                              <Typography level="body-sm" sx={{ mb: 1, color: 'neutral.700', lineHeight: 1.5 }}>
                                {description}
                              </Typography>
                              
                              {/* Full message and evidence - collapsed by default */}
                              <details>
                                <summary style={{ cursor: 'pointer', fontSize: '0.75rem', color: 'var(--joy-palette-neutral-500)', marginTop: '4px' }}>
                                  📋 View Details
                                </summary>
                                <Box sx={{ mt: 1, p: 1.5, bgcolor: 'background.level2', borderRadius: 'sm' }}>
                                  <Typography level="body-sm" sx={{ mb: 1.5, whiteSpace: 'pre-line', lineHeight: 1.6 }}>
                                    {body}
                                  </Typography>
                                  {item.evidence && item.evidence.length > 0 && (
                                    <Box sx={{ mt: 1 }}>
                                      <Typography level="body-xs" sx={{ fontWeight: 600, mb: 0.5, color: 'neutral.600' }}>
                                        📊 Chart Analysis:
                                      </Typography>
                                      <Typography level="body-xs" sx={{ opacity: 0.8 }}>
                                        {item.evidence.join(' • ')}
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              </details>
                            </Box>
                          </Card>
                        );
                      })}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </Box>
          ) : (
            <Card 
              variant="soft" 
              color="neutral"
              sx={{ 
                p: 3, 
                textAlign: 'center',
                border: '2px dashed',
                borderColor: 'neutral.outlinedBorder'
              }}
            >
              <Typography level="h4" sx={{ color: 'neutral.700', mb: 1 }}>
                No Planetary Combinations Detected
              </Typography>
              <Typography level="body-sm" color="neutral" sx={{ opacity: 0.8 }}>
                Your chart doesn't have the specific planet combinations for these predictions.
              </Typography>
            </Card>
          )}
        </TabPanel>
      </Tabs>

    </Box>
  );
}

// Export memoized version for performance
export default React.memo(FeedsList);

